import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import SingUpModal from '@/components/Modals/SingUpModal';
import { toast } from '@/components/toast';
import { useCreateAgent, useCreateKnowledge, useUpdateKnowledgeAndDocuments } from '@/pages/agents/api';
import {
    AGENT_DEFAULT_NAME,
    AGENT_INSTRUCTION_MAX_LENGTH,
    AGENT_NAME_MAX_LENGTH,
    AGENT_STARTER_MESSAGE_MAX_LENGTH,
    AGENT_STARTER_MESSAGE_MAX_WORD,
    AGENT_TEXT_KNOWLEDGE_MAX_LENGTH,
    MAX_FILES_UPLOAD,
} from '@/pages/agents/constants';
import { getAgentLanguage, useGetCurrentAgentsPresenter } from '@/pages/agents/hooks';
import { defaultAgentGreeting, getAgentBgObject, validateAgentGrounding } from '@/pages/agents/utils';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useClearEditor } from '@/state/agent';
import { isImageProcessing } from '@/state/presenters/presenters';
import { useAgentEditorStore } from '@/state/root';
import { rootStore } from '@/state/root/store';
import { AppRoutes } from '@/utils/types';
import { AgentCreateTabType, AgentKnowledgeSource, Presenter, StreamTextToSpeechVoice } from '@d-id/types';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { HeaderButton } from '../common/HeaderButton';

const prepareAgentMixpanelData = (presenter: Presenter | undefined) => {
    const { agentEditorStore } = rootStore;
    const { inputAgent, ttsSelectedVoice, uploadingDocumentFiles, llm, starterQuestions } = agentEditorStore;

    return {
        agentId: inputAgent.id,
        presenter: presenter?.url,
        driver: presenter?.driverId,
        type: presenter?.type,
        presenterType: presenter?.presenterType,
        presenterLabels: presenter?.labels,
        isDefault: presenter?.isDefault,
        agentName: inputAgent.preview_name,
        selectedVoice: ttsSelectedVoice,
        documentLength: uploadingDocumentFiles.length,
        agentGroundingType: llm.template,
        presenterId: presenter?.id,
        behavior: {
            role: llm.prompt_customization?.role,
            personality: llm.prompt_customization?.personality,
            instructions: llm.instructions,
        },
        temperature: llm.temperature,
        knowledgeSource: llm.prompt_customization?.knowledge_source,
        starterQuestionsCount: starterQuestions.length,
        topicsToAvoid: llm.prompt_customization?.topics_to_avoid,
        maxResponseLength: llm.prompt_customization?.max_response_length,
        ...(inputAgent.from_template ? { from: 'agent-template' } : {}),
    };
};

export const CreateButton = observer(() => {
    const agentEditorStore = useAgentEditorStore();

    const navigate = useNavigate();
    const mixpanel = useAnalytics();
    const { isAnonymous } = useAuth();

    const { clearEditor } = useClearEditor();
    const { isLoading: isAgentLoading, mutateAsync: createAgentMutation } = useCreateAgent();
    const { mutateAsync: createKnowledge } = useCreateKnowledge();
    const { mutateAsync: updateKnowledgeAndDocuments } = useUpdateKnowledgeAndDocuments();
    const presenter: Presenter | undefined = useGetCurrentAgentsPresenter();

    const isLoadingPresenter = useRecoilValue(isImageProcessing);
    const [isOpenModal, setModalState] = useState(false);
    const [unknownError, setUnknownError] = useState(false);

    // TODO: move this logic to a computed value in the store and update it according to the new values
    const disabled = useMemo(
        () =>
            !agentEditorStore.ttsSelectedVoice ||
            agentEditorStore.isStarterQuestionsEdited ||
            isLoadingPresenter ||
            (agentEditorStore.llm.instructions?.length ?? 0) > AGENT_INSTRUCTION_MAX_LENGTH ||
            (agentEditorStore.llm.prompt_customization?.knowledge_source == AgentKnowledgeSource.BaseKnowledge &&
                (agentEditorStore.baseKnowledge?.length ?? 0) > AGENT_TEXT_KNOWLEDGE_MAX_LENGTH) ||
            agentEditorStore.inputAgent.preview_name.length > AGENT_NAME_MAX_LENGTH ||
            isAgentLoading ||
            agentEditorStore.isUpdatingKnowledge ||
            (presenter?.id && agentEditorStore.backgroundProcessingPresenters.has(presenter.id)) ||
            agentEditorStore.starterQuestions.some(
                question =>
                    question.length > AGENT_STARTER_MESSAGE_MAX_LENGTH || question.split(' ').length > AGENT_STARTER_MESSAGE_MAX_WORD
            ),
        [
            isLoadingPresenter,
            isAgentLoading,
            agentEditorStore.isUpdatingKnowledge,
            agentEditorStore.uploadingDocumentFiles,
            agentEditorStore.isStarterQuestionsEdited,
            agentEditorStore.ttsSelectedVoice,
            agentEditorStore.ttsSelectedLanguage,
            agentEditorStore.starterQuestions,
            agentEditorStore.inputAgent.preview_name,
            agentEditorStore.llm.instructions,
            agentEditorStore.llm.prompt_customization?.knowledge_source,
            agentEditorStore.baseKnowledge?.length,
            presenter?.id,
            agentEditorStore.backgroundProcessingPresenters,
            agentEditorStore.llm.model,
        ]
    );

    const onCloseModal = () => setModalState(false);

    const validateAgentFields = () => {
        if (!validateAgentGrounding()) {
            return false;
        }
        if (unknownError) {
            toast.error({ title: `Unknown error occur!`, toastId: 'agent-generate-unknown-error' });
            return false;
        }

        return true;
    };

    const handleGenerate = async () => {
        const {
            inputAgent: { id, preview_name },
            initAgentState: { knowledgeId },
            ttsSelectedVoice,
            uploadingDocumentFiles,
            deletedDocumentFiles,
            llm,
            starterQuestions,
            greetings,
            triggers,
            presenterIsStitch,
            ttsSelectedLanguage,
            baseKnowledge,
            presenterBackgroundSourceUrl,
            presenterBackgroundColor,
        } = agentEditorStore;

        const mixpanelData = prepareAgentMixpanelData(presenter);
        mixpanel?.track('agent-generate', {
            event: 'click',
            ...mixpanelData,
        });

        if (isAnonymous) {
            setModalState(true);
            return;
        }

        if (!validateAgentFields()) {
            return;
        }

        try {
            agentEditorStore.setIsCreating(true);

            const voiceProvider = {
                type: ttsSelectedVoice?.provider,
                voice_id: ttsSelectedVoice?.id,
                access: ttsSelectedVoice?.access,
                language: getAgentLanguage(ttsSelectedVoice, ttsSelectedLanguage),
            } as StreamTextToSpeechVoice; // todo - remove this type cast
            let knowledgeID = undefined;
            const agentName = preview_name || AGENT_DEFAULT_NAME;
            if (!knowledgeId && llm?.template !== 'assistant') {
                knowledgeID = await createKnowledge({ agentName });
            }

            const response = await createAgentMutation({
                presenter: presenter
                    ? {
                          ...presenter,
                          ...getAgentBgObject(presenter.type, presenterBackgroundColor, presenterBackgroundSourceUrl),
                      }
                    : undefined,
                agentName,
                llm: {
                    ...llm,
                    prompt_customization: { ...llm?.prompt_customization, prompt_knowledge: baseKnowledge ?? '' },
                },
                voiceProvider,
                ...(!!(knowledgeID || knowledgeId) && { knowledgeId: knowledgeID?.id ?? knowledgeId }),
                starter_message: starterQuestions,
                greetings: greetings || [defaultAgentGreeting(agentName)],
                agentId: id,
                presenterIsStitch,
                triggers,
            });

            await updateKnowledgeAndDocuments({
                knowledgeId: knowledgeID?.id ?? knowledgeId!,
                uploadingFiles: uploadingDocumentFiles,
                deletedFiles: deletedDocumentFiles,
            });

            mixpanel?.track('agent-generate', {
                event: 'success',
                ...mixpanelData,
                agentId: response?.id,
            });

            clearEditor();
            agentEditorStore.setIsCreating(false);
            navigate(AppRoutes.Agent, {
                state: { resetState: true, isAfterGenerate: true },
            });
        } catch (error) {
            mixpanel?.track('agent-generate', {
                event: 'error',
                ...mixpanelData,
            });
            agentEditorStore.setIsCreating(false);
        }
    };

    useEffect(() => {
        if (agentEditorStore.uploadingDocumentFiles.length > MAX_FILES_UPLOAD) {
            toast.error({ title: `Cannot upload more than ${MAX_FILES_UPLOAD} documents`, toastId: 'agent-generate-too-many-documents' });
            agentEditorStore.setErrorTabs([AgentCreateTabType.KnowledgeBase]);
        }
    }, [agentEditorStore.uploadingDocumentFiles]);

    return (
        <ErrorBoundary
            fallback={() => {
                setUnknownError(true);
                return <></>;
            }}
            beforeCapture={scope => scope.setTag('component', 'CreateButton')}>
            <HeaderButton
                id="create-agent-btn"
                variant="contained"
                color="secondary"
                onClick={handleGenerate}
                aria-label="Back"
                disabled={disabled}
                isLoading={agentEditorStore.isCreating}>
                Create agent
            </HeaderButton>
            <SingUpModal isOpen={isOpenModal} onClose={onCloseModal} from="agent-create" />
        </ErrorBoundary>
    );
});
